/**
 * MainCanvas — routes the active top-level tab to the correct view.
 *
 * All existing view components are REUSED here — not rewritten.
 * The canvas fills the remaining space between sidebar and (optional) chat panel.
 *
 * Tab → Component mapping:
 *   map       → MapView (existing)
 *   farm      → FarmView (DashboardPage + sub-tabs: fields, tasks, activities, etc.)
 *   chat      → ChatPage (existing, mobile-only tab)
 *   my_tasks  → WeeklyPlan (existing, worker view)
 *   reports   → DetailPage (advisor reports view)
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS_V2.md §6
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MapView from '../components/MapView';
import DashboardPage from '../components/DashboardPage';
import ChatPage from '../components/ChatPage';
import WeeklyPlan from '../components/WeeklyPlan';
import MyTasksView from '../components/MyTasksView';
import DetailPage from '../components/DetailPage';
import TodayView from '../views/TodayView';
import FarmOverviewGrid from '../views/FarmOverviewGrid';
import ReportsView from '../views/ReportsView';
import PoiListView from '../views/PoiListView';

export default function MainCanvas({
  activeTab,
  // Shared props passed through to child views
  token,
  // Dashboard / farm props
  dashboard,
  onCardTap,
  user,
  onUserUpdate,
  // Chat props (mobile tab)
  messages,
  sending,
  onSend,
  onSendFile,
  onNewSession,
  onMenuOpen,
  onShowDetail,
  appName,
  unreadCount,
  onBellClick,
  seasonalContext,
  // Map props
  onOpenFieldDetail,
  onCreateTask,
  onCreateActivity,
  onFieldCreated,
  // G.10: Map fly-to props
  pendingFlyToFieldId,
  onFlyToConsumed,
  // Detail / reports props
  detailType,
  detailParams,
  sprayWindows,
  goToChat,
  isDesktop,
  // Nav-1: phase gate
  isFeatureEnabled,
  // Context change callback (saves last view)
  onViewChange,
}) {

  const { t } = useTranslation();
  const [farmSubTab, setFarmSubTab] = useState(() => localStorage.getItem('aif_farmSubTab') || 'today');
  const changeFarmSubTab = (tab) => { setFarmSubTab(tab); localStorage.setItem('aif_farmSubTab', tab); };

  // Sanitize stale farmSubTab: if the stored tab is gated off, reset to 'today'
  useEffect(() => {
    const visibleFarmTabIds = [
      'today', 'farm',
      ...(isFeatureEnabled?.('pois_tab') ? ['pois'] : []),
      ...(isFeatureEnabled?.('reports_tab') ? ['reports'] : []),
    ];
    if (farmSubTab && !visibleFarmTabIds.includes(farmSubTab)) {
      setFarmSubTab('today');
      localStorage.setItem('aif_farmSubTab', 'today');
    }
  }, [farmSubTab, isFeatureEnabled]);

  const handleTabContent = () => {
    switch (activeTab) {
      case 'today':
        return (
          <TodayView token={token} weather={dashboard?.weather} onCardTap={onCardTap} />
        );

      case 'map':
        return (
          <MapView
            token={token}
            onOpenFieldDetail={onOpenFieldDetail}
            onCreateTask={onCreateTask}
            onCreateActivity={onCreateActivity}
            onFieldCreated={onFieldCreated}
            pendingFlyToFieldId={pendingFlyToFieldId}
            onFlyToConsumed={onFlyToConsumed}
            isFeatureEnabled={isFeatureEnabled}
          />
        );

      case 'farm':
        return (
          <div className="h-full flex flex-col">
            {/* Sub-tab bar */}
            <div className="flex border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-page)] flex-shrink-0 px-4">
              {[
                { id: 'today', label: t('today.tab') },
                { id: 'farm', label: t('today.farmTab') },
                { id: 'pois', label: t('poi.tab', 'POIs'), featureKey: 'pois_tab' },
                { id: 'reports', label: t('reports.tab'), featureKey: 'reports_tab' },
              ].filter(tab => !tab.featureKey || isFeatureEnabled?.(tab.featureKey)).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => changeFarmSubTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                    farmSubTab === tab.id
                      ? 'border-[var(--aif-color-primary)] text-[var(--aif-color-primary)]'
                      : 'border-transparent text-[var(--aif-text-muted)] hover:text-[var(--aif-text-secondary)]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* Sub-tab content */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {farmSubTab === 'today' && (
                <TodayView token={token} weather={dashboard?.weather} onCardTap={onCardTap} />
              )}
              {farmSubTab === 'farm' && (
                <FarmOverviewGrid dashboard={dashboard} tier={user?.subscription_tier || 'free'} onCardTap={onCardTap} token={token} user={user} isFeatureEnabled={isFeatureEnabled} />
              )}
              {farmSubTab === 'pois' && (
                <PoiListView token={token} />
              )}
              {farmSubTab === 'reports' && (
                <ReportsView token={token} onCardTap={onCardTap} />
              )}
            </div>
          </div>
        );

      case 'chat':
        return (
          <ChatPage
            messages={messages}
            sending={sending}
            onSend={onSend}
            onSendFile={onSendFile}
            onNewSession={onNewSession}
            onMenuOpen={onMenuOpen}
            onShowDetail={onShowDetail}
            appName={appName}
            token={token}
            unreadCount={unreadCount}
            onBellClick={onBellClick}
            seasonalContext={seasonalContext}
          />
        );

      case 'my_tasks':
        return (
          <MyTasksView
            token={token}
            userId={user?.id}
          />
        );

      case 'reports':
        return (
          <DetailPage
            detailType={detailType}
            detailParams={detailParams}
            weather={dashboard?.weather}
            subsidies={dashboard?.subsidies}
            sprayWindows={sprayWindows}
            fieldwork={dashboard?.fieldwork}
            market={dashboard?.market}
            finance={dashboard?.finance}
            cashflow={dashboard?.cashflow}
            token={token}
            goToChat={goToChat}
            isDesktop={isDesktop}
          />
        );

      default:
        // Unimplemented Phase 11+ tabs — placeholder
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="text-4xl mb-3">🚧</div>
            <p className="text-sm font-medium text-[var(--aif-text-primary)]">
              {activeTab}
            </p>
            <p className="text-xs text-[var(--aif-text-muted)] mt-1">
              {t('placeholder.comingSoon')}
            </p>
          </div>
        );
    }
  };

  return (
    <main
      className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col"
      data-testid="main-canvas"
      data-active-tab={activeTab}
    >
      {handleTabContent()}
    </main>
  );
}
